import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useCallback, useEffect, useRef } from 'react';
import { ProductColor, VariantDetails, VariantsByKey } from '../../../integrations/relewise/types/mapped';
import { NEW_BUYBOX_MODULE_NAME } from '../cus-new-buybox.const';

interface UseBuyboxMissingFieldsParams {
    hasLoadedProduct: boolean;
    variantsByKey: VariantsByKey | undefined;
    allColors: ProductColor[] | undefined;
    allSizes: string[] | undefined;
    displayName: string | undefined;
    selectedVariant: VariantDetails | undefined;
    telemetry: ITelemetry;
}

export const useLogBuyboxMissingFields = ({
    hasLoadedProduct,
    variantsByKey,
    allColors,
    allSizes,
    displayName,
    selectedVariant,
    telemetry
}: UseBuyboxMissingFieldsParams) => {
    const logError = useCallback(
        (message: string) => {
            const finalMessage = `[${NEW_BUYBOX_MODULE_NAME}] Encountered error during render: ${message}.`;
            telemetry.error(finalMessage);
            console.error(finalMessage);
        },
        [telemetry]
    );

    useEffect(
        function logGeneralMissingFields() {
            if (!hasLoadedProduct) {
                return;
            }
            if (!variantsByKey || !Object.keys(variantsByKey).length) {
                logError('Missing product variants');
            }
            if (!allColors?.length) {
                logError('Missing product colors');
            }
            if (!allSizes?.length) {
                logError('Missing product sizes');
            }
            if (!displayName) {
                logError('Missing product displayName');
            }
        },
        [hasLoadedProduct, variantsByKey, allColors, allSizes, displayName, logError]
    );

    const loggedVariantIds = useRef<Set<string>>(new Set());
    useEffect(
        function logSelectedVariantMissingFields() {
            if (!hasLoadedProduct) {
                return;
            }
            if (!selectedVariant) {
                logError('Missing selected variant');
                return;
            }
            if (loggedVariantIds.current.has(selectedVariant.relewiseVariantId)) {
                return;
            }

            if (!selectedVariant.concatenatedId) {
                logError(`Missing concatenatedId on variant ${selectedVariant.relewiseVariantId}`);
            }
            if (!selectedVariant.displayName) {
                logError(`Missing displayName on variant ${selectedVariant.relewiseVariantId}`);
            }
            if (!selectedVariant.finalPrice && !selectedVariant.basePrice) {
                logError(`Missing price on variant ${selectedVariant.relewiseVariantId}`);
            }
            if (!selectedVariant.color) {
                logError(`Missing color on variant ${selectedVariant.relewiseVariantId}`);
            }
            if (!selectedVariant.size) {
                logError(`Missing size on variant ${selectedVariant.relewiseVariantId}`);
            }
            loggedVariantIds.current.add(selectedVariant.relewiseVariantId);
        },
        [selectedVariant, hasLoadedProduct, logError]
    );
};
